/**
 * NOS-240: Shared pagination for public listing pages (blog, media).
 * Previous/Next plus a jump-to-page input (clamped, Enter/blur commits),
 * and a hook that scrolls the listing top back into view on page change,
 * respecting prefers-reduced-motion.
 */

import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface ListingPaginationProps {
  page: number
  totalPages: number
  /** Commits a page change. The jump input always uses this; Previous/Next do too unless getPageHref is set. */
  onPageChange: (page: number) => void
  /** When provided, Previous/Next render as deep-linkable <Link>s (e.g. media listing). */
  getPageHref?: (page: number) => string
  /** Button size, to match the surrounding listing's controls. */
  size?: "default" | "sm"
  className?: string
}

/**
 * Scrolls `targetRef` (the listing top anchor) into view whenever `page`
 * changes after mount — deliberately not the full window, so listings that
 * sit below hero/chrome land on their own top. Instant when the user
 * prefers reduced motion, smooth otherwise.
 */
export function useScrollToListingTop(
  page: number,
  targetRef: React.RefObject<HTMLElement | null>
) {
  const prevPageRef = useRef<number | null>(null)
  useEffect(() => {
    const prev = prevPageRef.current
    prevPageRef.current = page
    if (prev === null || prev === page) return
    const target = targetRef.current
    if (!target || typeof target.scrollIntoView !== "function") return
    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" })
  }, [page, targetRef])
}

export function ListingPagination({
  page,
  totalPages,
  onPageChange,
  getPageHref,
  size = "default",
  className = "mt-10 flex items-center justify-center gap-2",
}: ListingPaginationProps) {
  const [draft, setDraft] = useState(String(page))

  /** Keep the input in sync when the page changes via prev/next or URL. */
  useEffect(() => {
    setDraft(String(page))
  }, [page])

  if (totalPages <= 1) return null

  const commitDraft = () => {
    const parsed = parseInt(draft, 10)
    if (Number.isNaN(parsed)) {
      setDraft(String(page))
      return
    }
    const clamped = Math.min(Math.max(parsed, 1), totalPages)
    setDraft(String(clamped))
    if (clamped !== page) onPageChange(clamped)
  }

  const prevNext = (target: number, label: string) =>
    getPageHref ? (
      <Button variant="outline" size={size} asChild>
        <Link to={getPageHref(target)}>{label}</Link>
      </Button>
    ) : (
      <Button variant="outline" size={size} onClick={() => onPageChange(target)}>
        {label}
      </Button>
    )

  return (
    <nav className={className} aria-label="Pagination">
      {page > 1 ? prevNext(page - 1, "Previous") : null}
      <span className="flex items-center gap-1.5 px-2 text-sm text-muted-foreground">
        Page
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={`Page, ${page} of ${totalPages}`}
          className="h-8 w-14 px-1 text-center text-sm"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDraft()
          }}
          onBlur={commitDraft}
        />
        of {totalPages}
      </span>
      {page < totalPages ? prevNext(page + 1, "Next") : null}
    </nav>
  )
}
